import { Component, Inject, OnInit, ChangeDetectionStrategy, signal } from '@angular/core'
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog'
import { Roolit, UserFeature } from '@lemon/core-firebase/model/kayttaja'
import { firstValueFrom } from 'rxjs'
import { AsiakasService } from '../_angular/service/asiakas/asiakas.service'
import { FirebaseLemonator } from '../_angular/service/firebase-lemonator.service'
import { KirjanpitajaService } from '../_angular/service/kirjanpitaja/kirjanpitaja.service'
import { Kayttaja } from '../_jaettu-lemonator/model/asiakas'

export interface KayttajaHistoriaDialogData {
  asiakasAvain: string
  kayttaja: Kayttaja
}

interface FieldChanges {
  etunimi: boolean
  sukunimi: boolean
  email: boolean
  puhelinnumero: boolean
  aktiivinen: boolean
  roolit: boolean
  features: boolean
}

interface ViewableHistoria extends Kayttaja {
  paivittajanNimi: string
  roolitViewable: RooliWithStatus[]
  featuresViewable: FeatureWithStatus[]
  fieldChanges: FieldChanges
}

interface RooliWithStatus {
  nimi: string
  status: 'added' | 'removed' | 'unchanged'
}

interface FeatureWithStatus {
  nimi: string
  status: 'added' | 'removed' | 'unchanged'
}

@Component({
  templateUrl: './kayttaja.historia.dialog.html',
  styleUrls: ['./kayttaja.historia.dialog.css'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class KayttajaHistoriaDialog implements OnInit {
  readonly historia = signal<ViewableHistoria[]>([])
  readonly loading = signal(true)

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: KayttajaHistoriaDialogData,
    private _dialogRef: MatDialogRef<KayttajaHistoriaDialog>,
    private _firebaseLemonator: FirebaseLemonator,
    private _kirjanpitajaService: KirjanpitajaService,
    private _asiakasService: AsiakasService,
  ) {}

  async ngOnInit() {
    await this.loadHistory()
  }

  async loadHistory() {
    this.loading.set(true)

    try {
      const historiaPromise = this._firebaseLemonator
        .firestoreCollection<Kayttaja>(`asiakkaat/${this.data.asiakasAvain}/kayttajat/${this.data.kayttaja.avain}/historia`)
        .orderBy('paivitetty', 'desc')
        .get()

      const kirjanpitajienNimitiedotMapPromise = firstValueFrom(this._kirjanpitajaService.kirjanpitajienNimitiedotMapObservable)

      const [historia, kirjanpitajienNimitiedotMap] = await Promise.all([historiaPromise, kirjanpitajienNimitiedotMapPromise])

      // Process all historia items in a single loop
      const result: ViewableHistoria[] = []

      for (let i = 0; i < historia.length; i++) {
        const current = historia[i]
        const previous = historia[i + 1] // History is ordered desc by date

        // Map paivittaja to display name
        let paivittajanNimi: string
        if (current.paivittaja === 'lemonator_system') {
          paivittajanNimi = 'Lemonatorin taustajärjestelmä'
        } else {
          const paivittaja = kirjanpitajienNimitiedotMap.get(current.paivittaja)
          paivittajanNimi = paivittaja ? `${paivittaja.etunimi} ${paivittaja.sukunimi}` : 'Tuntematon'
        }

        // Compute role and feature changes by comparing with previous item
        const roolitViewable = this._computeRoolitWithStatus(current, previous)
        const featuresViewable = this._computeFeaturesWithStatus(current, previous)

        // Always include the first (oldest) record
        if (!previous) {
          result.push({
            ...current,
            paivittajanNimi,
            roolitViewable,
            featuresViewable,
            fieldChanges: {
              etunimi: false,
              sukunimi: false,
              email: false,
              puhelinnumero: false,
              aktiivinen: false,
              roolit: false,
              features: false,
            },
          })
          continue
        }

        // Compare relevant fields to detect changes
        const fieldChanges = this._computeFieldChanges(current, previous, roolitViewable, featuresViewable)
        const hasChanges = Object.values(fieldChanges).some(changed => changed)

        if (hasChanges) {
          result.push({
            ...current,
            paivittajanNimi,
            roolitViewable,
            featuresViewable,
            fieldChanges,
          })
        }
      }

      this.historia.set(result)
    } catch (error) {
      throw new Error('Error loading Kayttaja history:', error)
    } finally {
      this.loading.set(false)
    }
  }

  private _computeFieldChanges(current: Kayttaja, previous: Kayttaja, roolitViewable: RooliWithStatus[], featuresViewable: FeatureWithStatus[]): FieldChanges {
    return {
      etunimi: current.etunimi !== previous.etunimi,
      sukunimi: current.sukunimi !== previous.sukunimi,
      email: current.email !== previous.email,
      puhelinnumero: current.puhelinnumero !== previous.puhelinnumero,
      aktiivinen: current.aktiivinen !== previous.aktiivinen,
      roolit: roolitViewable.some(r => r.status !== 'unchanged'),
      features: featuresViewable.some(f => f.status !== 'unchanged'),
    }
  }

  close() {
    this._dialogRef.close()
  }

  getFeatures(item: ViewableHistoria): string {
    if (!item.features || Object.keys(item.features).length === 0) {
      return '-'
    }
    return JSON.stringify(item.features, null, 2)
  }

  private _computeRoolitWithStatus(current: Kayttaja, previous: Kayttaja): RooliWithStatus[] {
    const currentRoolit = current.roolit || {}
    const previousRoolit = previous?.roolit || {}

    const allRooliKeys: Set<Roolit> = new Set([...Object.keys(currentRoolit), ...Object.keys(previousRoolit)]) as Set<Roolit>

    const result: RooliWithStatus[] = []

    for (const key of allRooliKeys) {
      const currentValue = currentRoolit[key] || false
      const previousValue = previousRoolit[key] || false

      if (currentValue && !previousValue) {
        result.push({
          nimi: this._asiakasService.getKayttajaRooliDisplayName(key),
          status: 'added',
        })
      } else if (!currentValue && previousValue) {
        result.push({
          nimi: this._asiakasService.getKayttajaRooliDisplayName(key),
          status: 'removed',
        })
      } else if (currentValue && previousValue) {
        result.push({
          nimi: this._asiakasService.getKayttajaRooliDisplayName(key),
          status: 'unchanged',
        })
      }
    }
    return result
  }

  private _computeFeaturesWithStatus(current: Kayttaja, previous: Kayttaja): FeatureWithStatus[] {
    const currentFeatures = current.features || {}
    const previousFeatures = previous?.features || {}

    const allFeatureKeys: Set<UserFeature> = new Set([...Object.keys(currentFeatures), ...Object.keys(previousFeatures)]) as Set<UserFeature>

    const result: FeatureWithStatus[] = []

    for (const key of allFeatureKeys) {
      const currentValue = currentFeatures[key] || false
      const previousValue = previousFeatures[key] || false

      if (currentValue && !previousValue) {
        result.push({
          nimi: this._asiakasService.getKayttajaFeatureDisplayName(key),
          status: 'added',
        })
      } else if (!currentValue && previousValue) {
        result.push({
          nimi: this._asiakasService.getKayttajaFeatureDisplayName(key),
          status: 'removed',
        })
      } else if (currentValue && previousValue) {
        result.push({
          nimi: this._asiakasService.getKayttajaFeatureDisplayName(key),
          status: 'unchanged',
        })
      }
    }
    return result
  }
}
